import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import EditRoundedIcon from '@mui/icons-material/EditRounded';
import {
  Alert,
  Link as MuiLink,
  Box,
  Card,
  Chip,
  IconButton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { useSalesChannels, useUpdateSalesChannel } from '../../hooks';
import { useCatalogPermissions } from '../../lib/useCatalogPermissions';
import { useToast } from '../../lib/useToast';
import type { ToastSeverity } from '../../lib/useToast';
import type { SalesChannelResponse } from '../../model/types';
import { ChannelFormDialog, type ChannelFormValue } from './ChannelFormDialog';
import { CustomerGroupsPanel } from './CustomerGroupsPanel';

/**
 * Channels & modes panel — pricing-config surface: lists the pricing-side channel rows and edits
 * their pricing-specific config (mirror, resolution default). Channel IDENTITY (create/rename/
 * enable) lives in the unified registry (/settings/channels) and is PROJECTED into
 * web-price-engine-service — the create flow was removed here by plan-cleanup-001 (2026-08-25).
 *
 * Renamed "Channels" -> "Channels & groups" (the tab label, plan-platform-002 WP4.3) — this card
 * stays first, unchanged, with the new `CustomerGroupsPanel` card added below it (design spec
 * §8.10): both are price-slot dimensions, and this keeps groups out of a simple retailer's way.
 */
export function ChannelsPanel() {
  const { t } = useTranslation('catalog');
  const { data: channels, isLoading, isError, refetch } = useSalesChannels();
  const update = useUpdateSalesChannel();
  const { canPricingManage } = useCatalogPermissions();
  const { toast, success, error, clear } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SalesChannelResponse | undefined>();

  const onGroupsResult = (severity: ToastSeverity, message: string) =>
    severity === 'success' ? success(message) : error(message);

  if (isLoading) return <LoadingState label={t('channelsPanel.loading')} />;
  if (isError) {
    return (
      <ErrorState
        message={t('channelsPanel.loadError')}
        onRetry={() => void refetch()}
      />
    );
  }

  const list = channels ?? [];

  const handleSubmit = async (v: ChannelFormValue) => {
    if (!editing) return; // edit-only surface — identity creation lives in the registry
    try {
      await update.mutateAsync({
        code: editing.code,
        body: {
          name: v.name,
          active: v.active,
          isDefault: v.isDefault,
          mirrorsChannelCode: v.mirrorsChannelCode || undefined,
        },
      });
      success(t('channelsPanel.saved'));
      setDialogOpen(false);
      setEditing(undefined);
    } catch {
      error(t('channelsPanel.saveError'));
    }
  };

  return (
    <Box>
      {/* plan-tenant-001 Phase 5: channel identity moved to the unified registry (/settings/channels). */}
      <Alert severity="info" sx={{ mb: 2 }} data-testid="channels-identity-moved-banner">
        {t('channelsPanel.identityMovedBanner')}{' '}
        <MuiLink component={RouterLink} to="/settings/channels">
          →
        </MuiLink>
      </Alert>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t('channelsPanel.description')}
        </Typography>
      </Stack>
      {list.length === 0 ? (
        <EmptyState
          title={t('channelsPanel.empty.title')}
          description={t('channelsPanel.empty.description')}
        />
      ) : (
        <Card variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('channelsPanel.table.channel')}</TableCell>
                <TableCell>{t('channelsPanel.table.code')}</TableCell>
                <TableCell>{t('channelsPanel.table.mode')}</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography fontWeight={600}>{c.name}</Typography>
                      {c.isDefault && (
                        <Chip size="small" label={t('channelsPanel.defaultChip')} variant="outlined" />
                      )}
                      {!c.active && (
                        <Chip size="small" color="default" label={t('channelsPanel.disabledChip')} />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <code>{c.code}</code>
                  </TableCell>
                  <TableCell>
                    {c.mirrorsChannelCode ? (
                      <Chip
                        size="small"
                        color="info"
                        label={t('channelsPanel.mirrorsChip', { code: c.mirrorsChannelCode })}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t('channelsPanel.ownPrices')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {canPricingManage && (
                      <IconButton
                        size="small"
                        aria-label={t('common:action.edit')}
                        onClick={() => {
                          setEditing(c);
                          setDialogOpen(true);
                        }}
                      >
                        <EditRoundedIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Box sx={{ mt: 2.5 }}>
        <CustomerGroupsPanel canManage={canPricingManage} onResult={onGroupsResult} />
      </Box>

      <ChannelFormDialog
        open={dialogOpen}
        editing={editing}
        channels={list}
        saving={update.isPending}
        onClose={() => {
          setDialogOpen(false);
          setEditing(undefined);
        }}
        onSubmit={handleSubmit}
      />
      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={clear}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={clear} severity={toast?.severity ?? 'info'} variant="filled" sx={{ width: '100%' }}>
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </Box>
  );
}
